import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SHOW_DO_LIST, IS_EDITING } from "../../../actions/types";
import { TransactionsService, DeliveryOrdersService, SaleAtExternalService } from "../../../services";
import {
    Backdrop, Box, Button, Card, CardContent, CardHeader, Checkbox,
    CircularProgress, Container, Dialog, DialogActions, DialogContent, DialogTitle,
    Grid, IconButton, InputAdornment, Tab, Tabs, TextField, Typography
} from '@material-ui/core';
import { SuccessDialog, ConfirmDialog, WarnDialog } from '../../../components/shared';
import Alert from '@material-ui/lab/Alert';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { MuiPickersUtilsProvider, KeyboardDatePicker, KeyboardDateTimePicker } from '@material-ui/pickers';
import * as yup from 'yup';
import { Formik, Form, useFormik } from 'formik';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import clsx from 'clsx';
import 'date-fns';
import DateFnsUtils from '@date-io/date-fns';
import allowAction from '../../../helpers/allowAction';
import startOfDay from 'date-fns/startOfDay';
import endOfDay from 'date-fns/endOfDay';
import format from 'date-fns/format';

//for floating point math
import Big from 'big.js';

import { red, green } from '@material-ui/core/colors';
import { Calendar, HelpCircleOutline, Magnify } from "mdi-material-ui";
import { cloneDeep } from 'lodash';

//For DO List Material Table
import MaterialTable, { MTableToolbar, MTableHeader } from 'material-table'
import TableIcons from '../../../materialTableIcons';
import '../../GridStyleOverrides/materialTableHome.css'


const useStyles = makeStyles(theme => ({
    //#region Edit/Create Dialog
    label: {
        paddingTop: '6px',
        paddingLeft: '4px',
        paddingRight: '4px',
        textTransform: 'uppercase',
        fontSize: 12,
    },
    gridContainer: {
        paddingTop: '4px',
        paddingBottom: '4px',
    },
    card: {
        backgroundColor: theme.palette.type === 'light' ? '#ebfaff' : theme.palette.background.paper,
        borderStyle: 'solid',
        borderColor: '#5a92af',
        borderWidth: 1,
        boxShadow: 'none',
        //'& .MuiIconButton-root': {
        //    paddingLeft: 0,
        //    paddingRight: 4,
        //    marginLeft: 0
        //},
        //'& .MuiAutocomplete-endAdornment': {
        //    marginLeft: 2
        //},
    },
    tabCard: {
        backgroundColor: theme.palette.type === 'light' ? 'white' : theme.palette.grey[700],
        //borderStyle: 'solid',
        //borderColor: '#5a92af',
        //borderWidth: 1,
        boxShadow: 'none',
        '& .MuiTabs-indicator': {
            display: 'none'
        }
    },
    activeTabTab: {
        backgroundColor: theme.palette.type === 'light' ? '#ebfaff' : theme.palette.background.paper,
        borderStyle: 'solid',
        borderColor: '#5a92af',
        borderWidth: 1,
        borderTopRightRadius: '6px',
        borderTopLeftRadius: '6px',
        borderBottomStyle: 'none',
        color: theme.palette.text.primary,
        zIndex: 1000,
    },
    activeTabPanel: {
        backgroundColor: theme.palette.type === 'light' ? '#ebfaff' : theme.palette.background.paper,
        borderStyle: 'solid',
        borderColor: '#5a92af',
        borderWidth: 1,
        borderTopRightRadius: '6px',
        borderBottomRightRadius: '6px',
        borderBottomLeftRadius: '6px',
        zIndex: 19,
        marginTop: -1
    },
    inactiveTabTab: {
        backgroundColor: theme.palette.type === 'light' ? 'white' : theme.palette.grey[700],
        color: theme.palette.text.secondary,
        //borderBottomStyle: 'solid',
        //borderColor: '#5a92af',
        //borderWidth: 1,
        marginBottom: 1
    },
    inactiveTabPanel: {
        borderTopRightRadius: '6px',
        borderBottomRightRadius: '6px',
        borderBottomLeftRadius: '6px',
    },
    cardHeader: {
        backgroundColor: '#5a92af',
        color: 'white',
        paddingTop: 0,
        paddingBottom: 0,
    },
    danger: {
        color: red[600],
    },
    buttonProgress: {
        color: '#2E94B9',
        position: 'absolute',
        top: '50%',
        left: '50%',
        marginTop: -12,
        marginLeft: -12,
    },
    buttonWrapper: {
        position: 'relative',
    },
    //#endregion

    //#region DeliveryOrders Dialog
    inputGridContainer: {
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        marginBottom: 0,
        justifyContent: 'space-between'
    },
    gridItem: {
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
    },
    inputLabel: {
        marginTop: 'auto',
        marginBottom: 'auto',
        color: theme.palette.text.secondary,
        width: '3rem',
        fontSize: 14,
        textTransform: 'uppercase',
        lineHeight: 1.75,
        fontWeight: 'bold',
    },
    deliveryOrdersTable: {
        minWidth: 650,
        paddingLeft: 0,
        paddingRight: 0,
        '& .MuiDataGrid-colCellWrapper': {
            backgroundColor: '#86C1D4'
        },
        '& .delivery-orders-table.error': {
            backgroundColor: red[100],
            color: red[600],
        },
        '& .delivery-orders-table.success': {
            backgroundColor: green[100],
            color: green[600],
        },
        '& .delivery-orders-table--header': {
            backgroundColor: '#86C1D4',
            color: 'white',
            fontWeight: 'bold',
            textTransform: 'uppercase'
        },
        '& svg.MuiSvgIcon-root.MuiDataGrid-iconSeparator': {
            display: 'none'
        }

    },
    table: {
        backgroundColor: theme.palette.background.paper,
    },
    backdrop: {
        zIndex: 5600,
    },
    //#endregion
}));

//yup validation schema
const validationSchema = yup.object().shape({
    transaction: yup.object().shape({
        bagLq: yup.string().required("Type is required"),
        doNo: yup.string().required("Delivery Order is required"),
        operationTeam: yup.string().required('Operation Team is required'),
        loadingDate: yup.date().typeError("Click the icon to choose your date").required("Loading Date is required"),
        shipmentType: yup.string().required("Shipment Type is required"),
        sealNo: yup.string().max(20, "Seal No must be at most 20 characters").required("Seal No is required"),
        container: yup.string().max(20, "Container must be at most 20 characters"),
        truckNo: yup.string().max(30, "Truck No/Name must be at most 30 characters").required("Truck No is required"),
        transportCom: yup.string().max(100, "Transport Company must be at most 100 characters"),
        driver: yup.string().max(30, "Driver name must be at most 30 characters").required("Driver Name is required"),
        weightOut: yup.number()
            .moreThan(yup.ref('weightIn'), 'Weight Out must be more than Weight In')
            .typeError('Weight Out must be numeric')
            .required("Weight Out is required"),
        weightIn: yup.number()
            .lessThan(yup.ref('weightOut'), 'Weight In must be less than Weight Out')
            .typeError('Weight In must be numeric')
            .required('Weight In is required.'),
        // "Check in Date/Time"
        weightInDatetime: yup.date()
            .typeError("Click the icon to choose your date")
            .required("Check In Date/Time is required"),
        // "End Date/Time"
        weightOutDatetime: yup.date()
            .typeError("Click the icon to choose your date")
            .min(yup.ref('weightInDatetime'), "End Date must be after Check In Date")
            .required("End Date/Time is required"),
    }),
    doItems: yup.array().of(
        yup.object().shape({
            doItem: yup.string().required("DO Item is required"),
            batchIndicator: yup.string().nullable(true),
            batchNoFrom: yup.string().when("batchIndicator", {
                is: "X",
                then: yup.string().max(10).required("Batch No is required"),
                otherwise: yup.string().nullable(true)
            }),
            higherLevelItem: yup.string().required("Higher Level Item is required"),
            pickQtySu: yup.number().typeError('Pick Qty must be numeric').required("Pick Qty is required"),
            netWeight: yup.number().typeError('Net Weight must be numeric').required("Net Weight is required"),
            grossWeight: yup.number().typeError('Gross Weight must be numeric').required("Gross Weight is required"),
            slocFrom: yup.string().required("Storage Location is required"),
            doInsTxt: yup.string(),
            coaNo: yup.string().max(20, "COA No must be at most 20 characters"),
            weightUnit: yup.string().required("Weight Unit is required")
        })
    ),
    sapDo: yup.array().of(
        yup.object().shape({
            salesOrg: yup.string().required("Sales Org is required"),
            sdDocCat: yup.string().required("Sales Category is required"),
        })
    )
});

function initialPickConvert(netWeight, weightUnit, salesUnit, materialNo) {
    let fromUnit = weightUnit;
    let toUnit = salesUnit;
    try {
        if (netWeight && fromUnit && toUnit && materialNo) {
            if (fromUnit === toUnit) {
                return netWeight;
            }
            let bigNetWeight = new Big(netWeight)
            //handle special case material
            if (materialNo === 'EGT1MG000X' && fromUnit === 'KG' && toUnit === 'L') {
                let specialfactor = new Big(1.1115);
                let newPick = bigNetWeight.times(specialfactor);
                return newPick;
            }
            //handle other materials
            let factor;
            if ((fromUnit === 'KG') && (toUnit === 'TO' || toUnit === 'MT')) {
                factor = 0.001;
            } else if (fromUnit === 'TO' && toUnit === 'KG') {
                factor = 1000;
            } else {
                factor = 1;
                console.log('Conversion factor not applied')
            }
            let newPick = bigNetWeight.times(factor);
            return newPick;
        }
    } catch (e) {
        console.log('initial pick conversion error', e)
    }
}

function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`full-width-tabpanel-${index}`}
            aria-labelledby={`full-width-tab-${index}`}

            {...other}
        >
            {value === index && (
                <Container style={{ paddingRight: 0, paddingLeft: 0 }}>
                    <Box p={2}>
                        {children}
                    </Box>
                </Container>
            )}
        </div>
    );
}

export default function SaleAtExternalEdit(props) {

    //Destructure passed props
    const {
        transactionData,
        setEditTicketData,
        open,
        onClose,
        handleSearchSubmit,
        setIsLoading
    } = props;
    const dispatch = useDispatch();
    const dropdownItemsStore = useSelector((state) => state.dropdownMenuItems);
    const isEditing = useSelector((state) => state.saleAtExternal.isEditing);
    const showDoList = useSelector((state) => state.saleAtExternal.showDoList);
    const classes = useStyles();

    const [isLoaded,] = useState(false);

    const [autocompleteData, setAutocompleteData] = useState({ operationTeam: dropdownItemsStore['operationTeam'][0] })
    const [autocompleteDataItems, setAutocompleteDataItems] = useState([])
    const [dropdownStatus, setDropdownStatus] = useState([]);
    const [dropdownStorageLocationItem, setDropdownStorageLocationItem] = useState(
        [
            { text: "Plant is required first", value: "" }
        ]
    );
    // Tab
    const [tabValue, setTabValue] = useState(0);

    const [isConvertingPick, setIsConvertingPick] = useState(false);
    const [formReady, setFormReady] = useState(false)

    const [formData, setFormData] = useState({
        transaction: { transactionType: 'Sale' },
        doItems: [],
        sapDo: []
    });
    // Dialog
    const [openDeliveryOrderDialog, setOpenDeliveryOrderDialog] = useState(!isEditing ? true : false);
    const [successDialog, setSuccessDialog] = useState({ isOpen: false, title: '', subTitle: '' });
    const [warnDialog, setWarnDialog] = useState({ isOpen: false, title: '', subTitle: '' });
    const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', subTitle: '' })

    //only used when creating
    const resetForm = (text, value) => {
        formik.resetForm()
        handleSetDoItems(formData)
        setAutocompleteData({
            bagLq: { text: text, value: value }, //persist user desired selection on reset
            operationTeam: dropdownItemsStore['operationTeam'][0], //restore default operation team after reset
        })
        formik.setFieldValue('transaction.bagLq', value) //set formik value to user desired selection after reset
        formik.setFieldValue('transaction.transactionType', 'Sale'); //set transaction
        convertNetWeightToPick()
    }

    //Buttons
    const [saveButton, setSaveButton] = useState({ disabled: false, loading: false });
    const [reSubmitButton, setReSubmitButton] = useState({ disabled: false, loading: false });

    //sets STATUS dropdown options according to current transaction.status
    const getDropdownOptionsForStatus = (status) => {
        let filtered;
        switch (status) {
            case '10': //Entry
                filtered = dropdownItemsStore.status.filter(o => {
                    let ok = ['10', '90']
                    return ok.includes(o.value);
                });
                break;
            case '20': //Re-Check
                filtered = dropdownItemsStore.status.filter(o => {
                    let ok = ['20']
                    return ok.includes(o.value);
                });
                break;
            case '30': //Submit
                filtered = dropdownItemsStore.status.filter(o => {
                    let ok = ['30', '90']
                    return ok.includes(o.value)
                });
                break;
            case '50': //Approved
                filtered = dropdownItemsStore.status.filter(o => {
                    let ok = ['50']
                    return ok.includes(o.value);
                });
                break;
            case '90': //Cancel
                filtered = dropdownItemsStore.status.filter(o => {
                    let ok = ['10', '90']
                    return ok.includes(o.value);
                });
                break;
            default:
                console.log("Something went wrong with the status options");
                return filtered = [];
        }
        return filtered;
    }

    // Formik does not handle null values
    //USE THIS TO ITERATE AND CHANGE NULL TO EMPTY STRING FOR FORMIK
    //Also gets material name for each DoItem
    const nullToStringAddMatDesc = (result) => {
        if (result.transaction) {
            for (var i in result.transaction) {
                if (result.transaction[i] === null) {
                    result.transaction[i] = ''
                }
            }
        }
        if (result.doItems) {
            for (let d = 0; d < result.doItems.length; d++) {
                if ((!result.doItems[d].materialName || result.doItems[d].materialName === '') && dropdownItemsStore.sapMaterial) {
                    let found = dropdownItemsStore.sapMaterial.find(m => m.value === result.doItems[d].materialNo)
                    if (found && found.text.includes(':')) {
                        result.doItems[d].materialName = found.text.split(':')[1].trimStart();
                    } else if (found && found.text.includes('-')) {
                        result.doItems[d].materialName = found.text.split('-')[1].trimStart();
                    } else {
                        result.doItems[d].materialName = ''
                    }
                }
                result.doItems[d].pickQtySu = initialPickConvert(
                    result.doItems[d].netWeight,
                    result.doItems[d].weightUnit,
                    result.doItems[d].salesUnit,
                    result.doItems[d].materialNo
                )
                for (var z in result.doItems[d]) {
                    if (result.doItems[d][z] === null) {
                        result.doItems[d][z] = ''
                    }
                }
            }
        }
        if (result.sapDo) {
            for (let s = 0; s < result.sapDo.length; s++) {
                for (var x in result.sapDo[i]) {
                    if (result.sapDo[i][x] === null) {
                        result.sapDo[i][x] = ''
                    }
                }
            }
        }
        return result;
    }

    // Some fields on the Oracle database don't allow empty strings (createDate, weightIn, etc...)
    //USE THIS TO ITERATE AND CHANGE STRING TO NULL FOR ORACLE
    const stringToNull = (result) => {
        if (result.transaction) {
            for (var i in result.transaction) {
                if (result.transaction[i] === '') {
                    result.transaction[i] = null
                }
            }
        }
        if (result.doItems) {
            for (let d = 0; d < result.doItems.length; d++) {
                for (var z in result.doItems[d]) {
                    if (result.doItems[d][z] === '') {
                        result.doItems[d][z] = null
                    }
                }
            }
        }
        if (result.sapDo) {
            for (let s = 0; s < result.sapDo.length; s++) {
                for (var x in result.sapDo[i]) {
                    if (result.sapDo[i][x] === '') {
                        result.sapDo[i][x] = null
                    }
                }
            }
        }
        return result;
    }

    const formik = useFormik({
        initialValues: formData,
        validationSchema: validationSchema,
        onSubmit: values => {
            try {
                stringToNull(values); //for Oracle
                //If any dates are objects, convert them to strings for HTTP request
                //format for backend to receive: yyyy-MM-ddTHH:mm:ss
                //example: 2021-08-15T07:37:00
                if (typeof values.transaction.loadingDate === 'object') {
                    let date = format(values.transaction.loadingDate, 'yyyy-MM-dd');
                    let time = format(values.transaction.loadingDate, 'HH:mm:ss');
                    values.transaction.loadingDate = date + 'T' + time;
                }
                if (typeof values.transaction.weightInDatetime === 'object') {
                    let date = format(values.transaction.weightInDatetime, 'yyyy-MM-dd');
                    let time = format(values.transaction.weightInDatetime, 'HH:mm:ss');
                    values.transaction.weightInDatetime = date + 'T' + time;
                }
                if (typeof values.transaction.weightOutDatetime === 'object') {
                    let date = format(values.transaction.weightOutDatetime, 'yyyy-MM-dd');
                    let time = format(values.transaction.weightOutDatetime, 'HH:mm:ss');
                    values.transaction.weightOutDatetime = date + 'T' + time;
                }

                TransactionsService.addEditTransactionWithItems(values).then(
                    (response) => {
                        let status = response.data?.data?.status;
                        if (status === '10') {
                            setEditTicketData(response.data.data);
                            dispatch({
                                type: IS_EDITING,
                                payload: true,
                            });

                        } else {
                            onClose();
                            handleSearchSubmit();
                            dispatch({
                                type: IS_EDITING,
                                payload: false,
                            });
                        }
                        setSaveButton({ disabled: false, loading: false })
                        setReSubmitButton({ disabled: false, loading: false })
                    });

            } catch (e) {
                console.log(e);
                setSaveButton({ disabled: false, loading: false })
                setReSubmitButton({ disabled: false, loading: false })
            }
        }
    });

    /*Gets the default object for the autocompletes (not storage) */
    function getAutocompleteTextAndValue(field, value) {
        var selection = dropdownItemsStore[field].filter(function (option) {
            return option.value === value;
        });
        if (selection.length === 0) {
            selection = [{ text: '', value: '' }];
        }
        return selection;
    }

    //only has effects when form is in EDIT MODE, after save entry, this will run to update the form status and add submit button
    useEffect(async () => {
        if (transactionData && transactionData.ticketId !== "") {
            //console.group('%c useEffect EDITING FORM', 'background-color:orange;color:purple')
            setIsLoading(true);
            let data = await SaleAtExternalService.getTransactionWithDoItems(transactionData.ticketId);
            if (data) {

                let result = nullToStringAddMatDesc(data.data) //for Formik

                //make sure do items are ordered correctly for tabs
                result.doItems = result.doItems.sort((a, b) => Number(a.doItem) - Number(b.doItem));
                setFormData(result);
                formik.setValues(result);

                setAutocompleteData({
                    status: getAutocompleteTextAndValue('status', result.transaction.status)[0],
                    bagLq: getAutocompleteTextAndValue('bagLq', result.transaction.bagLq)[0],
                    shipmentType: getAutocompleteTextAndValue('shipmentType', result.transaction.shipmentType)[0],
                    operationTeam: getAutocompleteTextAndValue('operationTeam', result.transaction.operationTeam)[0],
                    transportCom: getAutocompleteTextAndValue('sapVendorMatser', result.transaction.transportCom)[0],
                });

                // Autocomplete for STORAGE LOC
                let doItemAutocomplete = [];
                let dropdownStorageLocationItem = [];

                for (let i = 0; i < result.doItems.length; i++) {
                    //console.group('%c DO ITEMS index -->', 'background-color:purple;color:orange', i)
                    let hasSlocFrom = result.doItems[i].slocFrom
                    let findSlocFromInDB = getAutocompleteTextAndValue('sapStorageLocation', result.doItems[i].slocFrom)
                    //console.log('hasSlocFrom', hasSlocFrom)
                    if (hasSlocFrom) {
                        //console.log('findSlocFromInDb', findSlocFromInDB)
                        if (findSlocFromInDB[0].value === '') {
                            doItemAutocomplete.push({
                                slocFrom: { text: result.doItems[i].slocFrom, value: result.doItems[i].slocFrom }
                            })
                        } else {
                            doItemAutocomplete.push({
                                slocFrom: findSlocFromInDB[0],
                            });
                        }
                    }
                    // Create the list of storage locations for each items
                    var newStorageLocationList = dropdownItemsStore.sapStorageLocation.filter(function (location) {
                        return location.plant === result.sapDo[i].plant;
                    });


                    if (hasSlocFrom && findSlocFromInDB[0].value === '') {
                        let firstItem = {
                            plant: result.sapDo[i].plant,
                            text: result.doItems[i].slocFrom,
                            value: result.doItems[i].slocFrom
                        }
                        newStorageLocationList.unshift(firstItem)
                    }
                    //console.log('%c newStorageLocationList', 'background-color:green;color:purple', newStorageLocationList)
                    dropdownStorageLocationItem.push(newStorageLocationList);
                    //console.log('%c dropdownStorageLocationItem', 'color:coral', dropdownStorageLocationItem)
                    setDropdownStorageLocationItem(dropdownStorageLocationItem);
                    //console.log('doItemAutocomplete', doItemAutocomplete)
                    setAutocompleteDataItems(doItemAutocomplete);
                }
                setIsLoading(false);
            }
            //console.groupEnd();
        }
    }, [transactionData]);

    function convertNetWeightToPick() {
        setIsConvertingPick(true);
        let netWeight = formik.values.doItems[tabValue].netWeight;
        let fromUnit = formik.values.doItems[tabValue].weightUnit;
        let toUnit = formik.values.doItems[tabValue].salesUnit;
        let materialNo = formik.values.doItems[tabValue].materialNo;

        if (netWeight && fromUnit && toUnit && materialNo) {
            if (fromUnit === toUnit) {
                formik.setFieldValue(`doItems.${tabValue}.pickQtySu`, netWeight)
                setIsConvertingPick(false);
                return;
            }
            let bigNetWeight = new Big(netWeight)
            //handle special case material
            if (materialNo === 'EGT1MG000X' && fromUnit === 'KG' && toUnit === 'L') {
                let specialfactor = new Big(1.1115);
                let newPick = bigNetWeight.times(specialfactor);
                formik.setFieldValue(`doItems.${tabValue}.pickQtySu`, newPick)
                setIsConvertingPick(false);
                return;
            }
            //handle other materials
            let factor;
            if ((fromUnit === 'KG') && (toUnit === 'TO' || toUnit === 'MT')) {
                factor = 0.001;
            } else if (fromUnit === 'TO' && toUnit === 'KG') {
                factor = 1000;
            } else {
                factor = 1;
                console.log('Conversion factor not applied')
            }
            let newPick = bigNetWeight.times(factor);
            formik.setFieldValue(`doItems.${tabValue}.pickQtySu`, newPick)
            setIsConvertingPick(false);
            return;
        }
    }

    //if changes to formik.values.doItems[index].netWeight, pickQtySu is updated according to conversion factor
    let triggerConversion = formik.values.doItems[tabValue]?.netWeight
    useEffect(() => {
        if (formik.values?.doItems[tabValue]?.netWeight &&
            formik.values?.doItems[tabValue]?.weightUnit &&
            formik.values?.doItems[tabValue]?.salesUnit &&
            formik.values?.doItems[tabValue]?.materialNo) {
            convertNetWeightToPick()
        } else {
            return;
        }
    }, [triggerConversion, tabValue])

    let triggerFormReady = formData && formik.values.transaction && formik.values.doItems[tabValue] && formik.values.sapDo[tabValue]
    useEffect(() => {
        if (triggerFormReady) {
            setFormReady(true)
        } else {
            return;
        }
    }, [triggerFormReady])


    //#region Handlers
    const handleAutoCompleteChange = (target, fieldName) => {
        //console.group('handleAutoCompleteChange');
        var value = target !== null ? target.value : ''
        var text = target !== null ? target.text : ''

        if (fieldName === 'transaction.bagLq' && formik.values.transaction.bagLq) {
            setConfirmDialog({
                isOpen: true,
                title: 'Changing TYPE will reset the form...',
                subTitle: <><Box component="div">Click "Continue" to change TYPE and reset the form, or click "Cancel" to go back.</Box></>,
                onConfirm: () => {
                    resetForm(text, value)
                    setConfirmDialog({ ...confirmDialog, isOpen: false })
                }
            })
            return;
            //if (window.confirm("Changing type will reset the form")) {
            //    return resetForm(text, value)
            //}
        }
        formik.setFieldValue(`${fieldName}`, value);
        let splitfieldName = fieldName.split('.');
        if (splitfieldName.length > 0) {
            if (splitfieldName[0] === "transaction") {
                setAutocompleteData({ ...autocompleteData, [splitfieldName[1]]: { text: text, value: value } })
            }
            else if (splitfieldName[0] === "doItems") {
                //console.log('autocompleteDataItems', autocompleteDataItems)
                let newArr = cloneDeep(autocompleteDataItems); // copying the old datas array
                //newArr[index] = { slocFrom: [ { text, value} ] }
                newArr[splitfieldName[1]] = { [splitfieldName[2]]: { text: text, value: value } }; // update the array on the specific index
                setAutocompleteDataItems(newArr);
            }
        }
        //console.groupEnd();
    };
    const handleDateChange = (date, fieldName1, fieldName2) => {
        formik.setFieldValue(fieldName1, date);

        //used to update transaction.loadingDate when endDate is changed
        if (fieldName2) {
            formik.setFieldValue(fieldName2, date);
        }
    };
    const handleDeliveryOrdersDialogOpen = () => {
        setOpenDeliveryOrderDialog(true);
        dispatch({
            type: SHOW_DO_LIST,
            payload: true,
        });
    };
    const handleDeliveryOrdersDialogClose = () => {
        setOpenDeliveryOrderDialog(false);
        dispatch({
            type: SHOW_DO_LIST,
            payload: false,
        });
    };
    const handleEditDialogClose = () => {
        onClose();
    };
    const handleDeliveryOrdersDialogCancel = () => {
        setOpenDeliveryOrderDialog(false);
        dispatch({
            type: SHOW_DO_LIST,
            payload: false,
        });
        handleEditDialogClose();
    };
    const handleEditDialogSubmit = () => {  //"Save" button
        //REQUIREMENT: sub-items must have same storage
        if (formik.values.doItems) {
            let subs = formik.values.doItems.filter(f => f.higherLevelItem !== '000000');
            if (subs.length > 0) {
                let slocArr = subs.map(i => i.slocFrom)
                let compareAgainst = slocArr[0]
                for (let i = 0; i < slocArr.length; i++) {
                    if (slocArr[i] !== compareAgainst) {
                        setWarnDialog({
                            isOpen: true,
                            title: 'Storage Location Warning',
                            subTitle: <><Box component="div">Storage Location must be the same for all sub-items</Box></>,
                        })
                        return;
                    }
                }
            }
        }
        if (formData.transaction.status !== '90' && formik.values.transaction.status === '90' && !allowAction("301", "cancel")) {
            setWarnDialog({
                isOpen: true,
                title: 'Action Denied',
                subTitle: <><Box component="div">You don't have permission to cancel a ticket.  If this is a mistake, please contact your administrator.</Box></>,
            })
            return;
        }
        setSaveButton({ disabled: true, loading: true })
        formik.handleSubmit();
        if (Object.keys(formik.errors).length !== 0) {
            //find the index to indicate which tab the error is on
            //make the active tab the tab with the error
            if (formik.errors.doItems) {
                let exists = (i) => i !== undefined
                if (exists) {
                    setTabValue(formik.errors.doItems.findIndex(exists))
                }
            }
            setSaveButton({ disabled: false, loading: false })
        }
        else {
            handleSearchSubmit();
        }
    };
    const handleEditDialogReSubmit = () => {
        //REQUIREMENT: sub-items must have same storage
        if (formik.values.doItems) {
            let subs = formik.values.doItems.filter(f => f.higherLevelItem !== '000000');
            if (subs.length > 0) {
                let slocArr = subs.map(i => i.slocFrom)
                let compareAgainst = slocArr[0]
                for (let i = 0; i < slocArr.length; i++) {
                    if (slocArr[i] !== compareAgainst) {
                        return alert("Storage location must be the same for all sub-items")
                    }
                }
            }
        }
        setReSubmitButton({ disabled: true, loading: true })
        formik.setFieldValue('transaction.status', "30");
        formik.handleSubmit();
        if (Object.keys(formik.errors).length !== 0) {
            //find the index to indicate which tab the error is on
            //make the active tab the tab with the error
            if (formik.errors.doItems) {
                let indexWithError = (i) => i !== undefined
                if (indexWithError) {
                    setTabValue(formik.errors.doItems.findIndex(indexWithError))
                }
            }
            setReSubmitButton({ disabled: false, loading: false })
        }
    }
    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    //RUNS AFTER SELECTION FROM DO LIST DIALOG -- @param | shape: object | value: { transaction, doItems, sapDo } |-- source: DO LIST DIALOG
    //RUNS WHEN RESETTING FORM @param | shape: object | value: formData |-- source: self (useState)
    const handleSetDoItems = (dataFromSap) => {
        let deliveryOrder = dataFromSap
        if (deliveryOrder) {
            //console.log('%c IN handleSetDoItems --> deliveryOrder', 'background-color:pink;color:black', deliveryOrder)

            //make sure do items are in the correct order for tabs
            deliveryOrder.doItems = deliveryOrder.doItems.sort((a, b) => Number(a.doItem) - Number(b.doItem));

            deliveryOrder = nullToStringAddMatDesc(deliveryOrder);
            formik.setValues(deliveryOrder);
            if (dropdownItemsStore['operationTeam'][0]?.value) {
                formik.setFieldValue('transaction.operationTeam', dropdownItemsStore['operationTeam'][0].value)
            } else {
                //handle testing for users without operation team, change for production
                formik.setFieldValue('transaction.operationTeam', 'No Team')
            }
            // Autocomplete for the SAP Items (SLocFrom)
            let doItemAutocomplete = [];
            let dropdownStorageLocationItem = [];

            for (let i = 0; i < deliveryOrder.doItems.length; i++) {
                //console.group('%c DO ITEMS index -->', 'background-color:purple;color:orange', i)
                let hasSlocFrom = deliveryOrder.doItems[i].slocFrom
                let findSlocFromInDB = getAutocompleteTextAndValue('sapStorageLocation', deliveryOrder.doItems[i].slocFrom)
                //console.log('hasSlocFrom', hasSlocFrom)
                if (hasSlocFrom) {
                    //console.log('findSlocFromInDb', findSlocFromInDB)
                    if (findSlocFromInDB[0].value === '') {
                        doItemAutocomplete.push({
                            slocFrom: { text: deliveryOrder.doItems[i].slocFrom, value: deliveryOrder.doItems[i].slocFrom }
                        })
                    } else {
                        doItemAutocomplete.push({
                            slocFrom: findSlocFromInDB[0],
                        });
                    }
                } else {
                    doItemAutocomplete.push({
                        slocFrom: findSlocFromInDB[0]
                    })
                }
                // Create the list of storage locations for each items
                var newStorageLocationList = dropdownItemsStore.sapStorageLocation.filter(function (location) {
                    return location.plant === deliveryOrder.sapDo[i].plant;
                });


                if (hasSlocFrom && findSlocFromInDB[0].value === '') {
                    let firstItem = {
                        plant: deliveryOrder.sapDo[i].plant,
                        text: deliveryOrder.doItems[i].slocFrom,
                        value: deliveryOrder.doItems[i].slocFrom
                    }
                    newStorageLocationList.unshift(firstItem)
                }
                //console.log('%c newStorageLocationList', 'background-color:green;color:purple', newStorageLocationList)
                dropdownStorageLocationItem.push(newStorageLocationList);
                //console.log('%c dropdownStorageLocationItem', 'color:coral', dropdownStorageLocationItem)
                setDropdownStorageLocationItem(dropdownStorageLocationItem);
                //console.log('doItemAutocomplete', doItemAutocomplete)
                setAutocompleteDataItems(doItemAutocomplete);
            }
        }
    };
    //console.groupEnd();
    //#endregion

    //#region Logs
    //console.log('formikErrors', formik.errors.doItems)
    //console.log('formikValues', formik.values)
    //console.log('formData', formData)
    //console.log('%c autocompleteData--OTHERS', 'background-color:green;color:white', autocompleteData)
    //console.log('%c autocompleteData--STATUS', 'background-color:blue;color:white', dropdownItemsStore['status'])
    //console.log('%c autoCompleteSLOCFROM', 'background-color:gray;color:purple',autocompleteDataItems)
    //#endregion
    if (showDoList) {
        return (
            <Grid container spacing={2} style={{ height: '100%', width: '100%' }}>
                <Grid item >
                    <DeliveryOrdersDialog
                        handleSetDoItems={handleSetDoItems}
                        //setDoItems={setDoItemsFromSap}
                        isEditing={isEditing}
                        tabValue={tabValue}
                        setTabValue={setTabValue}
                        formData={formData}
                        setFormData={setFormData}
                        open={showDoList}
                        onClose={handleDeliveryOrdersDialogClose}
                        onCancelWithoutSelection={handleDeliveryOrdersDialogCancel}
                        resetForm={resetForm}
                    />
                </Grid>
            </Grid>
        )
    }
    if (formReady) {
        return (
            <>
                <Dialog scroll="paper" fullWidth={true} maxWidth={'xl'} style={{ maxWidth: 1500, margin: 'auto' }} open={open} onClose={handleEditDialogClose} disableBackdropClick>
                    <DialogTitle
                        id="form-dialog-title">
                        <Grid container>
                            <Grid item xs={6}>
                                {formData?.transaction?.ticketId ? <>Ticket ID: {formData?.transaction?.ticketId}</> : <>Create a new ticket</>}
                            </Grid>
                        </Grid>
                    </DialogTitle>
                    <DialogContent dividers={true}>
                        <Formik>
                            <Form onSubmit={formik.handleSubmit}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Card className={classes.card}>
                                            <CardHeader
                                                className={classes.cardHeader}
                                                action={
                                                    <IconButton aria-label="settings">
                                                        <HelpCircleOutline />
                                                    </IconButton>
                                                }
                                                title="DELIVERY ORDER DATA"
                                            />
                                            <CardContent>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12} md={6} lg={3}>
                                                        {isEditing ?
                                                            <Grid container className={classes.gridContainer}>
                                                                <Grid item xs={5} lg={3} className={classes.label}>Status<Typography component="span" color="error">*</Typography></Grid>
                                                                <Grid item xs={7}>
                                                                    <Autocomplete
                                                                        id="statusAutocomplete"
                                                                        name="statusAutocomplete"
                                                                        closeIcon=""
                                                                        disabled={formData?.transaction?.status === '50' ? true : false}
                                                                        //disabled={true}
                                                                        options={getDropdownOptionsForStatus(formData?.transaction?.status)}
                                                                        value={autocompleteData.status ? { text: autocompleteData.status.text, value: autocompleteData.status.value } : ""}
                                                                        getOptionLabel={(option) => option.text ? option.text : ''}
                                                                        getOptionSelected={(option, value) => option.value === value.value}
                                                                        renderOption={option => <>{option.text}</>}
                                                                        onChange={(event, newValue) => {
                                                                            handleAutoCompleteChange(newValue, 'transaction.status');
                                                                        }}
                                                                        renderInput={(params) => <TextField {...params}
                                                                            size="small"
                                                                            variant="outlined"
                                                                            error={formik.touched.status && Boolean(formik.errors.status)}
                                                                            helperText={formik.touched.status && formik.errors.status}
                                                                        />}
                                                                    />
                                                                </Grid>
                                                            </Grid>
                                                            : null}
                                                        <Grid container className={classes.gridContainer}>
                                                            <Grid item xs={5} lg={3} className={classes.label}>Type<Typography component="span" color="error">*</Typography></Grid>
                                                            <Grid item xs={7}>
                                                                <Autocomplete
                                                                    id="bagLqAutocomplete"
                                                                    name="bagLqAutocomplete"
                                                                    closeIcon=""
                                                                    disabled={formData?.transaction?.status === '50' || isEditing ? true : false}
                                                                    options={dropdownItemsStore["bagLq"]}
                                                                    value={autocompleteData.bagLq ? { text: autocompleteData.bagLq.text, value: autocompleteData.bagLq.value } : ""}
                                                                    getOptionLabel={(option) => option.text ? option.text : ''}
                                                                    getOptionSelected={(option, value) => option.value === value.value}
                                                                    renderOption={option => <>{option.text}</>}
                                                                    onChange={(event, newValue) => {
                                                                        handleAutoCompleteChange(newValue, 'transaction.bagLq');
                                                                    }}
                                                                    renderInput={(params) => <TextField {...params}
                                                                        size="small"
                                                                        variant="outlined"
                                                                        error={formik.touched.transaction && formik.touched.transaction?.bagLq && formik.errors.transaction && formik.errors.transaction.bagLq ? Boolean(formik.errors.transaction.bagLq) : false}
                                                                        helperText={formik.touched.transaction && formik.touched.transaction?.bagLq && formik.errors.transaction && formik.errors.transaction.bagLq ? formik.errors.transaction.bagLq : ""}
                                                                    />}
                                                                />
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                    <Grid item xs={12} md={6} lg={4}>
                                                        <Grid container className={classes.gridContainer}>
                                                            <Grid item xs={5} lg={2} className={classes.label}>Do No<Typography component="span" color="error">*</Typography></Grid>
                                                            <Grid item xs={7} lg={8}>
                                                                {/*<FormControl className={clsx(classes.margin, classes.textField)} variant="outlined">*/}
                                                                <TextField
                                                                    readOnly={formData?.transaction?.status !== '50' ? true : false}
                                                                    disabled={formData?.transaction?.status === '50' ? true : false}
                                                                    id="doNo"
                                                                    name="doNo"
                                                                    size="small"
                                                                    variant="outlined"
                                                                    className={classes.textfield}
                                                                    value={formik.values.transaction.doNo}
                                                                    onChange={formik.handleChange}
                                                                    error={formik.touched.doNo && Boolean(formik.errors.doNo)}
                                                                    margin="dense"
                                                                    InputProps={{
                                                                        endAdornment:
                                                                            <InputAdornment position="end">
                                                                                <IconButton
                                                                                    aria-label="delivery orders"
                                                                                    disabled={formData?.transaction?.status === '50' ? true : false}
                                                                                    onClick={handleDeliveryOrdersDialogOpen}
                                                                                    edge="end"
                                                                                >
                                                                                    <Magnify />
                                                                                </IconButton>
                                                                            </InputAdornment>
                                                                    }}
                                                                />
                                                                {/*<FormHelperText error={formik.touched.doNo && Boolean(formik.errors.doNo)} id="accountId-error">*/}
                                                                {/*    {formik.touched.doNo && formik.errors.doNo}*/}
                                                                {/*</FormHelperText>*/}
                                                                {/*  </FormControl>*/}
                                                            </Grid>
                                                        </Grid>
                                                        <Grid container className={classes.gridContainer}>
                                                            <Grid item xs={5} lg={2} className={classes.label}>COA No</Grid>
                                                            <Grid item xs={7} lg={8}>
                                                                <TextField
                                                                    disabled={formData?.transaction?.status === '50' ? true : false}
                                                                    fullWidth
                                                                    value={formik.values.transaction.coaNo}
                                                                    onChange={formik.handleChange}
                                                                    name={`transaction.coaNo`}
                                                                    size="small"
                                                                    variant="outlined"
                                                                />
                                                            </Grid>
                                                        </Grid>
                                                        <Grid container className={classes.gridContainer}>
                                                            <Grid item xs={5} lg={2} className={classes.label}>Remark</Grid>
                                                            <Grid item xs={7} lg={8}>
                                                                <TextField
                                                                    fullWidth
                                                                    disabled={formData?.transaction?.status === '50' ? true : false}
                                                                    value={formik.values.transaction.doInsTxt}
                                                                    onChange={formik.handleChange}
                                                                    id="remark"
                                                                    name={`transaction.doInsTxt`}
                                                                    size="small"
                                                                    variant="outlined"
                                                                />
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                    <Grid item xs={12} md={6} lg={4}>
                                                        <Grid container className={classes.gridContainer}>
                                                            <Grid item xs={5} lg={4} className={classes.label}>Operation Team<Typography component="span" color="error">*</Typography></Grid>
                                                            <Grid item xs={7} lg={8}>
                                                                <Autocomplete
                                                                    id="operationTeamAutocomplete"
                                                                    name="operationTeamAutocomplete"
                                                                    disabled={formData?.transaction?.status === '50' ? true : false}
                                                                    closeIcon=""
                                                                    options={dropdownItemsStore["operationTeam"]}
                                                                    value={autocompleteData.operationTeam ? { text: autocompleteData.operationTeam.text, value: autocompleteData.operationTeam.value } : ""}
                                                                    getOptionLabel={(option) => option.text}
                                                                    renderOption={option => <>{option.text}</>}
                                                                    onChange={(event, newValue) => {
                                                                        handleAutoCompleteChange(newValue, 'transaction.operationTeam');
                                                                    }}
                                                                    renderInput={(params) => <TextField {...params}
                                                                        size="small"
                                                                        variant="outlined"
                                                                        error={formik.touched.transaction && formik.errors.transaction && formik.errors.transaction.operationTeam ? Boolean(formik.errors.transaction.operationTeam) : false}
                                                                        helperText={formik.touched.transaction && formik.errors.transaction && formik.errors.transaction.operationTeam ? formik.errors.transaction.operationTeam : ""}
                                                                    />}
                                                                />
                                                            </Grid>
                                                        </Grid>
                                                        <Grid container className={classes.gridContainer}>
                                                            <Grid item xs={5} lg={4} className={classes.label}>Loading Date/Time<Typography component="span" color="error">*</Typography></Grid>
                                                            <Grid item xs={7} lg={8}>
                                                                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                                    <Grid container justify="space-around">
                                                                        <KeyboardDateTimePicker
                                                                            disabled={true}
                                                                            disableToolbar={false}
                                                                            ampm={false}
                                                                            autoOk={true}
                                                                            size="small"
                                                                            fullWidth
                                                                            inputVariant="outlined"
                                                                            format="dd/MM/yyyy HH:mm"
                                                                            //minDate={formik.values.loadingDate !== null ? new Date("1900-01-01") : new Date(dateNow.getFullYear(), dateNow.getMonth() - 1, dateNow.getDate())}
                                                                            //maxDate={formik.values.loadingDate !== null ? new Date("2100-01-01") : new Date(dateNow.getFullYear(), dateNow.getMonth(), dateNow.getDate())}
                                                                            onChange={(date) => handleDateChange(date, 'transaction.loadingDate')}
                                                                            value={formik.values.transaction.loadingDate}
                                                                            keyboardIcon={<Calendar />}
                                                                            KeyboardButtonProps={{
                                                                                'aria-label': 'change date',
                                                                            }}
                                                                            error={formik.touched.transaction && formik.errors.transaction && formik.errors.transaction.loadingDate ? Boolean(formik.errors.transaction.loadingDate) : false}
                                                                            helperText={formik.touched.transaction && formik.errors.transaction && formik.errors.transaction.loadingDate ? formik.errors.transaction.loadingDate : ''}
                                                                        />
                                                                    </Grid>
                                                                </MuiPickersUtilsProvider>
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Card className={classes.tabCard}>
                                            <CardContent style={{ paddingLeft: 0, paddingRight: 0, paddingBottom: 0 }}>
                                                <Tabs
                                                    value={tabValue}
                                                    //indicatorColor="primary"
                                                    //textColor="primary"
                                                    onChange={handleTabChange}

                                                >
                                                    {formData && formData.doItems ? formData.doItems.map((item, index) => (
                                                        <Tab
                                                            label={item.doItem}
                                                            disableRipple
                                                            disableFocusRipple
                                                            key={`${index}${item.doItem}`}
                                                            className={index === tabValue ? classes.activeTabTab : classes.inactiveTabTab} />
                                                    )) : <></>}
                                                </Tabs>
                                                {formData && formData.doItems ? formData.doItems.map((item, index) => (
                                                    <TabPanel value={tabValue} index={index} key={`${item.doItem}${index}`} className={index === tabValue ? classes.activeTabPanel : classes.inactiveTabPanel} style={index !== 0 ? { borderTopLeftRadius: '6px' } : null}>
                                                        <Grid container spacing={2} lg={12} xl={12} >
                                                            <Grid item xs={12} md={6} lg={3} xl={3}>
                                                                <Grid container className={classes.gridContainer}>
                                                                    <Grid item xs={5} lg={4} className={classes.label}>Do Item<Typography component="span" color="error">*</Typography></Grid>
                                                                    <Grid item xs={7} lg={8}>
                                                                        <TextField
                                                                            id="doItem"
                                                                            name="doItem"
                                                                            size="small"
                                                                            variant="outlined"
                                                                            disabled
                                                                            className={classes.textfield}
                                                                            value={formik.values.doItems[index].doItem}
                                                                        />
                                                                    </Grid>
                                                                </Grid>
                                                                <Grid container className={classes.gridContainer}>
                                                                    <Grid item xs={5} lg={4} className={classes.label}>Sales Org<Typography component="span" color="error">*</Typography></Grid>
                                                                    <Grid item xs={7} lg={8}>
                                                                        <TextField
                                                                            disabled
                                                                            value={formData?.sapDo[index] ? formData.sapDo[index].salesOrg : ""}
                                                                            id={`sapDo.${index}.salesOrg`}
                                                                            name={`sapDo.${index}.salesOrg`}
                                                                            size="small"
                                                                            variant="outlined"
                                                                            error={formik.touched.salesOrg && Boolean(formik.errors.salesOrg)}
                                                                            helperText={formik.touched.salesOrg && formik.errors.salesOrg}
                                                                        />
                                                                    </Grid>
                                                                </Grid>
                                                                <Grid container className={classes.gridContainer}>
                                                                    <Grid item xs={5} lg={4} className={classes.label}>Sales Category<Typography component="span" color="error">*</Typography></Grid>
                                                                    <Grid item xs={7} lg={8}>
                                                                        <TextField
                                                                            disabled
                                                                            value={formData?.sapDo[index] ? formData.sapDo[index].sdDocCat : ""}
                                                                            name={`sapDo.${index}.sdDocCat`}
                                                                            size="small"
                                                                            variant="outlined"
                                                                            error={formik.touched.sapDo && formik.errors.sapDo && formik.errors.sapDo[index] ? Boolean(formik.errors.sapDo[index].sdDocCat) : false}
                                                                            helperText={formik.touched.sapDo && formik.errors.sapDo && formik.errors.sapDo[index] ? formik.errors.sapDo[index].sdDocCat : ""}
                                                                        />
                                                                    </Grid>
                                                                </Grid>
                                                                <Grid container className={classes.gridContainer}>
                                                                    <Grid item xs={5} lg={4} className={classes.label}>Higher Level Item<Typography component="span" color="error">*</Typography></Grid>
                                                                    <Grid item xs={7} lg={8}>
                                                                        <TextField
                                                                            disabled
                                                                            value={formData?.doItems[index] ? formData.doItems[index].higherLevelItem : ""}
                                                                            id="higherLevelItem"
                                                                            name="higherLevelItem"
                                                                            size="small"
                                                                            variant="outlined"
                                                                            error={formik.touched.higherLevelItem && Boolean(formik.errors.higherLevelItem)}
                                                                            helperText={formik.touched.higherLevelItem && formik.errors.higherLevelItem}
                                                                        />
                                                                    </Grid>
                                                                </Grid>
                                                            </Grid>
                                                            <Grid item xs={12} md={6} lg={4} xl={4}>
                                                                <Grid container className={classes.gridContainer}>
                                                                    <Grid item xs={5} lg={4} className={classes.label}>Material No<Typography component="span" color="error">*</Typography></Grid>
                                                                    <Grid item xs={7} lg={8}>
                                                                        <TextField
                                                                            disabled
                                                                            fullWidth
                                                                            value={formData?.doItems[index] ? formData.doItems[index].materialNo : ""}
                                                                            name={`doItems.${index}.materialNo`}
                                                                            size="small"
                                                                            variant="outlined"
                                                                            error={formik.touched.materialNo && Boolean(formik.errors.materialNo)}
                                                                            helperText={formik.touched.materialNo && formik.errors.materialNo}
                                                                        />
                                                                    </Grid>
                                                                </Grid>
                                                                <Grid container className={classes.gridContainer}>
                                                                    <Grid item xs={5} lg={4} className={classes.label}>Material Name<Typography component="span" color="error">*</Typography></Grid>
                                                                    <Grid item xs={7} lg={8}>
                                                                        <TextField
                                                                            disabled
                                                                            fullWidth
                                                                            value={formData?.doItems[index] ? formData?.doItems[index].materialName : ""}
                                                                            name={`doItems.${index}.materialName`}
                                                                            size="small"
                                                                            variant="outlined"
                                                                            error={formik.touched.materialNo && Boolean(formik.errors.materialNo)}
                                                                            helperText={formik.touched.materialNo && formik.errors.materialNo}
                                                                        />
                                                                    </Grid>
                                                                </Grid>
                                                                <Grid container className={classes.gridContainer}>
                                                                    <Grid item xs={5} lg={4} className={classes.label}>Plant<Typography component="span" color="error">*</Typography></Grid>
                                                                    <Grid item xs={7} lg={8}>
                                                                        <TextField
                                                                            disabled
                                                                            fullWidth
                                                                            value={formData?.sapDo[index] ? formData.sapDo[index].plant : ""}
                                                                            name={`sapDo.${index}.plant`}
                                                                            size="small"
                                                                            variant="outlined"
                                                                        //error={formik.touched.plantFrom && Boolean(formik.errors.plantFrom)}
                                                                        //helperText={formik.touched.plantFrom && formik.errors.plantFrom}
                                                                        />
                                                                    </Grid>
                                                                </Grid>
                                                                <Grid container className={classes.gridContainer}>
                                                                    <Grid item xs={5} lg={4} className={classes.label}>Storage Loc<Typography component="span" color="error">*</Typography></Grid>
                                                                    <Grid item xs={7} lg={8}>
                                                                        <Autocomplete
                                                                            fullWidth
                                                                            id="storageLocAutocomplete"
                                                                            name={`doItems.slocFrom`}
                                                                            disabled={
                                                                                formData && formData?.transaction.status !== '50' &&
                                                                                    (formData?.doItems[index]?.slocFrom === '' ||
                                                                                        formik.values?.transaction.bagLq !== 'BAG') ? false : true}

                                                                            closeIcon=""
                                                                            options={dropdownStorageLocationItem && dropdownStorageLocationItem[index] ? dropdownStorageLocationItem[index] : [{ text: "Select a Plant first", value: "" }]}
                                                                            value={autocompleteDataItems && autocompleteDataItems[index] && autocompleteDataItems[index].slocFrom ? { text: autocompleteDataItems[index].slocFrom.text, value: autocompleteDataItems[index].slocFrom.value } : ""}
                                                                            getOptionLabel={(option) => option.text ? option.text : ""}
                                                                            getOptionSelected={(option, value) => option.value === value.value}
                                                                            renderOption={option => <>{option.text}</>}
                                                                            onChange={(event, newValue) => {
                                                                                handleAutoCompleteChange(newValue, `doItems.${index}.slocFrom`);
                                                                            }}
                                                                            renderInput={(params) => <TextField {...params}
                                                                                size="small"
                                                                                variant="outlined"
                                                                                error={formik.touched.doItems && formik.errors.doItems && formik.errors.doItems[index] && formik.errors.doItems[index].slocFrom ? Boolean(formik.errors.doItems[index].slocFrom) : false}
                                                                                helperText={formik.touched.doItems && formik.errors.doItems && formik.errors.doItems[index] && formik.errors.doItems[index].slocFrom ? formik.errors.doItems[index].slocFrom : ''}
                                                                            />}
                                                                        />
                                                                    </Grid>
                                                                </Grid>
                                                                <Grid container className={classes.gridContainer}>
                                                                    <Grid item xs={5} lg={4} className={classes.label}>Batch Indicator<Typography component="span" color="error">*</Typography></Grid>
                                                                    <Grid item xs={7} lg={8}>
                                                                        <TextField
                                                                            disabled
                                                                            fullWidth
                                                                            value={formik.values?.doItems[index] ? formik.values?.doItems[index].batchIndicator : ""}
                                                                            name={`doItems.${index}.batchIndicator`}
                                                                            size="small"
                                                                            variant="outlined"
                                                                            error={formik.touched.doItems && formik.errors.doItems && formik.errors.doItems[index] ? Boolean(formik.errors.doItems[index].batchIndicator) : false}
                                                                            helperText={formik.touched.doItems && formik.errors.doItems && formik.errors.doItems[index] ? formik.errors.doItems[index].batchIndicator : ""}
                                                                        />
                                                                    </Grid>
                                                                </Grid>
                                                                <Grid container className={classes.gridContainer}>
                                                                    <Grid item xs={5} lg={4} className={classes.label}>Batch<Typography component="span" color="error">*</Typography></Grid>
                                                                    <Grid item xs={7} lg={8}>
                                                                        <TextField
                                                                            fullWidth
                                                                            disabled={formik.values.doItems && formik.values.doItems[index] && formData?.transaction?.status !== '50' && formik.values.doItems[index].batchIndicator ? false : true}
                                                                            value={formik.values?.doItems && formik.values.doItems[index] ? formik.values.doItems[index].batchNoFrom : ""}
                                                                            name={`doItems.${index}.batchNoFrom`}
                                                                            size="small"
                                                                            variant="outlined"
                                                                            onChange={formik.handleChange}
                                                                            error={formik.touched.doItems && formik.errors.doItems && formik.errors.doItems[index] && formik.errors.doItems[index].batchNoFrom ? Boolean(formik.errors.doItems[index].batchNoFrom) : false}
                                                                            helperText={formik.touched.doItems && formik.errors.doItems && formik.errors.doItems[index] && Boolean(formik.errors.doItems[index].batchNoFrom) ? formik.errors.doItems[index].batchNoFrom : ""}
                                                                        />
                                                                    </Grid>
                                                                </Grid>

                                                            </Grid>
                                                            <Grid item xs={12} md={6} lg={2} xl={2}>
                                                                <Grid container className={classes.gridContainer}>
                                                                    <Grid item xs={5} lg={5} className={classes.label}>Base Unit<Typography component="span" color="error">*</Typography></Grid>
                                                                    <Grid item xs={7} lg={7}>
                                                                        <TextField
                                                                            fullWidth
                                                                            disabled
                                                                            value={formik.values?.doItems[index] ? formik.values?.doItems[index].baseUnit : ""}
                                                                            name={`doItems.${index}.baseUnit`}
                                                                            size="small"
                                                                            variant="outlined"
                                                                            error={formik.touched.doItems && formik.errors.doItems && formik.errors.doItems[index] ? Boolean(formik.errors.doItems[index].baseUnit) : false}
                                                                            helperText={formik.touched.doItems && formik.errors.doItems && formik.errors.doItems[index] ? formik.errors.doItems[index].baseUnit : ""}
                                                                        />
                                                                    </Grid>
                                                                </Grid>
                                                                <Grid container className={classes.gridContainer}>
                                                                    <Grid item xs={5} lg={5} className={classes.label}>ACT QTY DELV SU<Typography component="span" color="error">*</Typography></Grid>
                                                                    <Grid item xs={7} lg={7}>
                                                                        <TextField
                                                                            fullWidth
                                                                            disabled
                                                                            name={`sapDo.${index}.actQtyDelvSu`}
                                                                            size="small"
                                                                            variant="outlined"
                                                                            value={formik.values?.doItems[index] ? formik.values?.doItems[index].actQtyDelvSu : ""}
                                                                        />
                                                                    </Grid>
                                                                </Grid>
                                                                <Grid container className={classes.gridContainer}>
                                                                    <Grid item xs={5} lg={5} className={classes.label}>ACT QTY DELV SKU<Typography component="span" color="error">*</Typography></Grid>
                                                                    <Grid item xs={7} lg={7}>
                                                                        <TextField
                                                                            fullWidth
                                                                            disabled={true}
                                                                            name={`sapDo.${index}.actQtyDelvSku`}
                                                                            size="small"
                                                                            variant="outlined"
                                                                            value={formik.values?.doItems[index] ? formik.values.doItems[index].pickQtySu : ""}
                                                                        />
                                                                    </Grid>
                                                                </Grid>
                                                                <Grid container className={classes.gridContainer}>
                                                                    <Grid item xs={5} lg={5} className={classes.label}>Pick Qty<Typography component="span" color="error">*</Typography></Grid>
                                                                    <Grid item xs={7} lg={7}>
                                                                        <TextField
                                                                            fullWidth
                                                                            disabled={formData?.transaction?.status !== '50' && formik.values.transaction?.bagLq === "LQ" ? false : true}
                                                                            name={`doItems.${index}.pickQtySu`}
                                                                            size="small"
                                                                            variant="outlined"
                                                                            value={formik.values?.doItems[index] && !isConvertingPick ? formik.values.doItems[index].pickQtySu : ""}
                                                                            onChange={formik.handleChange}
                                                                            onKeyDown={(evt) => ["e", "E"].includes(evt.key) && evt.preventDefault()}
                                                                            error={formik.errors.doItems && formik.errors.doItems[index] ? Boolean(formik.errors.doItems[index].pickQtySu) : false}
                                                                            helperText={formik.errors.doItems && formik.errors.doItems[index] ? formik.errors.doItems[index].pickQtySu : ""}
                                                                        />
                                                                    </Grid>
                                                                </Grid>
                                                                <Grid container className={classes.gridContainer}>
                                                                    <Grid item xs={5} lg={5} className={classes.label}>Sale Unit<Typography component="span" color="error">*</Typography></Grid>
                                                                    <Grid item xs={7} lg={7}>
                                                                        <TextField
                                                                            fullWidth
                                                                            disabled
                                                                            value={formik.values.doItems[index] ? formik.values.doItems[index].salesUnit : ""}
                                                                            name={`doItems.${index}.salesUnit`}
                                                                            size="small"
                                                                            variant="outlined"
                                                                            error={formik.errors.doItems && formik.errors.doItems[index] ? Boolean(formik.errors.doItems[index].salesUnit) : false}
                                                                            helperText={formik.errors.doItems && formik.errors.doItems[index] ? formik.errors.doItems[index].salesUnit : ""}
                                                                        />
                                                                    </Grid>
                                                                </Grid>
                                                            </Grid>
                                                            <Grid item xs={12} md={6} lg={3} xl={3} >
                                                                <Grid container className={classes.gridContainer}>
                                                                    <Grid item xs={5} lg={4} className={classes.label}>Net Weight<Typography component="span" color="error">*</Typography></Grid>
                                                                    <Grid item xs={7} lg={8}>
                                                                        <TextField
                                                                            fullWidth
                                                                            type='number'
                                                                            disabled={formData?.transaction?.status !== '50' && formik.values?.transaction.bagLq === "LQ" ? false : true}
                                                                            value={formik.values?.doItems[index] ? formik.values.doItems[index].netWeight : ""}
                                                                            name={`doItems.${index}.netWeight`}
                                                                            size="small"
                                                                            variant="outlined"
                                                                            onChange={formik.handleChange}
                                                                            onKeyDown={(evt) => ["e", "E"].includes(evt.key) && evt.preventDefault()}
                                                                            error={formik.errors.doItems && formik.errors.doItems[index] ? Boolean(formik.errors.doItems[index].netWeight) : false}
                                                                            helperText={formik.errors.doItems && formik.errors.doItems[index] ? formik.errors.doItems[index].netWeight : ""}
                                                                        />
                                                                    </Grid>
                                                                </Grid>
                                                                <Grid container className={classes.gridContainer}>
                                                                    <Grid item xs={5} lg={4} className={classes.label}>Gross Weight<Typography component="span" color="error">*</Typography></Grid>
                                                                    <Grid item xs={7} lg={8}>
                                                                        <TextField
                                                                            fullWidth
                                                                            disabled={formData?.transaction?.status !== '50' && formik.values?.transaction.bagLq === "LQ" ? false : true}
                                                                            value={formik.values?.doItems[index] ? formik.values.doItems[index].grossWeight : ""}
                                                                            name={`doItems.${index}.grossWeight`}
                                                                            size="small"
                                                                            variant="outlined"
                                                                            onChange={formik.handleChange}
                                                                            onKeyDown={(evt) => ["e", "E"].includes(evt.key) && evt.preventDefault()}
                                                                            error={formik.errors.doItems && formik.errors.doItems[index] ? Boolean(formik.errors.doItems[index].grossWeight) : false}
                                                                            helperText={formik.errors.doItems && formik.errors.doItems[index] ? formik.errors.doItems[index].grossWeight : ""}
                                                                        />
                                                                    </Grid>
                                                                </Grid>
                                                                <Grid container className={classes.gridContainer}>
                                                                    <Grid item xs={5} lg={4} className={classes.label}>Weight Unit<Typography component="span" color="error">*</Typography></Grid>
                                                                    <Grid item xs={7} lg={8}>
                                                                        <TextField
                                                                            fullWidth
                                                                            disabled
                                                                            value={formik.values?.doItems[index] ? formik.values.doItems[index].weightUnit : ""}
                                                                            name={`doItems.${index}.weightUnit`}
                                                                            size="small"
                                                                            variant="outlined"
                                                                            error={formik.errors.doItems && formik.errors.doItems[index] ? Boolean(formik.errors.doItems[index].weightUnit) : false}
                                                                            helperText={formik.errors.doItems && formik.errors.doItems[index] ? formik.errors.doItems[index].weightUnit : ""}
                                                                        />
                                                                    </Grid>
                                                                </Grid>
                                                            </Grid>
                                                        </Grid>
                                                    </TabPanel>
                                                )) : <></>}
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} sm={8} md={10} lg={10} xl={10}>
                                        <Card className={classes.card}>
                                            <CardHeader
                                                className={classes.cardHeader}
                                                title="TRANSPORTATION DETAILS"
                                            />
                                            <CardContent>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12} md={6} lg={6} xl={6} >
                                                        <Grid container className={classes.gridContainer}>
                                                            <Grid item xs={5} lg={3} className={classes.label}>Shipment Type<Typography component="span" color="error">*</Typography></Grid>
                                                            <Grid item xs={7} lg={7}>
                                                                <Autocomplete
                                                                    id="shipmentTypeAutocomplete"
                                                                    name="shipmentTypeAutocomplete"
                                                                    closeIcon=""
                                                                    fullWidth
                                                                    disabled={formData?.transaction?.status === '50' ? true : false}
                                                                    options={dropdownItemsStore["shipmentType"]}
                                                                    value={autocompleteData.shipmentType ? { text: autocompleteData.shipmentType.text, value: autocompleteData.shipmentType.value } : ""}
                                                                    getOptionLabel={(option) => option.text ? option.text : ''}
                                                                    getOptionSelected={(option, value) => option.value === value.value}
                                                                    renderOption={option => <>{option.text}</>}
                                                                    onChange={(event, newValue) => {
                                                                        handleAutoCompleteChange(newValue, 'transaction.shipmentType');
                                                                    }}
                                                                    renderInput={(params) => <TextField {...params}
                                                                        size="small"
                                                                        variant="outlined"
                                                                        error={formik.touched.transaction && formik.touched.transaction?.shipmentType && formik.errors.transaction && formik.errors.transaction.shipmentType ? Boolean(formik.errors.transaction.shipmentType) : false}
                                                                        helperText={formik.touched.transaction && formik.touched.transaction?.shipmentType && formik.errors.transaction && formik.errors.transaction.shipmentType ? formik.errors.transaction.shipmentType : ""}
                                                                    />}
                                                                />
                                                            </Grid>
                                                        </Grid>
                                                        <Grid container className={classes.gridContainer}>
                                                            <Grid item xs={5} lg={3} className={classes.label}>Container ID </Grid>
                                                            <Grid item xs={7} lg={7}>
                                                                <TextField
                                                                    disabled={formData?.transaction?.status === '50' ? true : false}
                                                                    fullWidth
                                                                    name={`transaction.container`}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    className={classes.textfield}
                                                                    value={formik.values?.transaction ? formik.values.transaction.container : ""}
                                                                    onChange={formik.handleChange}
                                                                />
                                                            </Grid>
                                                        </Grid>
                                                        <Grid container className={classes.gridContainer}>
                                                            <Grid item xs={5} lg={3} className={classes.label}>Seal No<Typography component="span" color="error">*</Typography></Grid>
                                                            <Grid item xs={7} lg={7}>
                                                                <TextField
                                                                    disabled={formData?.transaction?.status === '50' ? true : false}
                                                                    fullWidth
                                                                    name={`transaction.sealNo`}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    className={classes.textfield}
                                                                    value={formik.values?.transaction ? formik.values.transaction.sealNo : ""}
                                                                    onChange={formik.handleChange}
                                                                    error={formik.touched.transaction && formik.touched.transaction?.sealNo && formik.errors.transaction && formik.errors.transaction.sealNo ? Boolean(formik.errors.transaction.sealNo) : false}
                                                                    helperText={formik.touched.transaction && formik.touched.transaction?.sealNo && formik.errors.transaction && formik.errors.transaction.sealNo ? formik.errors.transaction.sealNo : ''}
                                                                />
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                    <Grid item xs={12} md={6} lg={6} xl={5}>
                                                        <Grid container className={classes.gridContainer}>
                                                            <Grid item xs={5} lg={3} xl={5} className={classes.label}>Check In Date/Time<Typography component="span" color="error">*</Typography></Grid>
                                                            <Grid item xs={7} lg={7} >
                                                                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                                    <Grid container justify="space-around">
                                                                        <KeyboardDateTimePicker
                                                                            disabled={formData?.transaction?.status === '50' ? true : false}
                                                                            disableToolbar={false}
                                                                            ampm={false}
                                                                            autoOk={true}
                                                                            size="small"
                                                                            fullWidth
                                                                            inputVariant="outlined"
                                                                            format="dd/MM/yyyy HH:mm"
                                                                            name="transaction.weightInDatetime"
                                                                            maxDate={formik.values?.transaction.weightOutDatetime ? formik.values.transaction.weightOutDatetime : new Date()}
                                                                            value={formik.values?.transaction && formik.values?.transaction?.weightInDatetime ? formik.values.transaction.weightInDatetime : ''}
                                                                            maxDateMessage={"Check-in Date cannot come after the End Date"}
                                                                            onChange={(date) => handleDateChange(date, 'transaction.weightInDatetime')}
                                                                            keyboardIcon={<Calendar />}
                                                                            KeyboardButtonProps={{
                                                                                'aria-label': 'change date',
                                                                            }}
                                                                            error={formik.touched.transaction && formik.errors.transaction && formik.errors.transaction.weightInDateTime ? Boolean(formik.errors.transaction.weightInDateTime) : false}
                                                                            helperText={formik.touched.transaction && formik.errors.transaction && formik.errors.transaction.weightInDateTime ? formik.errors.transaction.weightInDateTime : ''}
                                                                        />
                                                                    </Grid>
                                                                </MuiPickersUtilsProvider>
                                                            </Grid>
                                                        </Grid>
                                                        <Grid container className={classes.gridContainer}>
                                                            <Grid item xs={5} lg={3} xl={5} className={classes.label}>End Date/Time<Typography component="span" color="error">*</Typography></Grid>
                                                            <Grid item xs={7} lg={7}>
                                                                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                                    <Grid container justify="space-around">
                                                                        <KeyboardDateTimePicker
                                                                            disabled={formData?.transaction?.status === '50' ? true : false}
                                                                            disableToolbar={false}
                                                                            ampm={false}
                                                                            autoOk={true}
                                                                            size="small"
                                                                            fullWidth
                                                                            inputVariant="outlined"
                                                                            format="dd/MM/yyyy HH:mm"
                                                                            name="transaction.weightOutDatetime"
                                                                            minDate={formik.values?.transaction ? formik.values.transaction.weightInDatetime : new Date()}
                                                                            minDateMessage={'End Date cannot come before the Check-in Date'}
                                                                            onChange={(date) => handleDateChange(date, 'transaction.weightOutDatetime', 'transaction.loadingDate')}
                                                                            value={formik.values?.transaction && formik.values?.transaction?.weightOutDatetime ? formik.values.transaction.weightOutDatetime : ''}
                                                                            keyboardIcon={<Calendar />}
                                                                            KeyboardButtonProps={{
                                                                                'aria-label': 'change date',
                                                                            }}
                                                                            error={formik.touched.transaction && formik.errors.transaction && formik.errors.transaction.weightOutDatetime ? Boolean(formik.errors.transaction.weightOutDatetime) : false}
                                                                            helperText={formik.touched.transaction && formik.errors.transaction && formik.errors.transaction.weightOutDatetime ? formik.errors.transaction.weightOutDatetime : ''}
                                                                        />
                                                                    </Grid>
                                                                </MuiPickersUtilsProvider>
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                    <Grid item xs={12} md={6} lg={8} xl={8}>
                                                        <Grid container className={classes.gridContainer}>
                                                            <Grid item xs={5} lg={3} xl={3} className={classes.label}>Truck Company</Grid>
                                                            <Grid item xs={7} lg={9} xl={9}>
                                                                <Autocomplete
                                                                    id="truckCompanyAutocomplete"
                                                                    name="truckCompanyAutocomplete"
                                                                    disabled={formData?.transaction?.status === '50' ? true : false}
                                                                    closeIcon=""
                                                                    autoComplete
                                                                    autoSelect
                                                                    options={dropdownItemsStore["sapVendorMatser"]
                                                                        .sort((a, b) => {
                                                                            if (a.value < b.value) {
                                                                                return -1;
                                                                            }
                                                                            if (a.value > b.value) {
                                                                                return 1;
                                                                            }
                                                                            return 0;
                                                                        })
                                                                    }
                                                                    value={autocompleteData.transportCom ? { text: autocompleteData.transportCom.text, value: autocompleteData.transportCom.value } : ""}
                                                                    getOptionLabel={(option) => option.text ? option.text : ''}
                                                                    getOptionSelected={(option, value) => option.value === value.value}
                                                                    renderOption={option => <>{option.text}</>}
                                                                    onChange={(event, newValue) => {
                                                                        handleAutoCompleteChange(newValue, 'transaction.transportCom');
                                                                    }}
                                                                    renderInput={(params) => <TextField {...params}
                                                                        size="small"
                                                                        variant="outlined"
                                                                    />}
                                                                />
                                                            </Grid>
                                                        </Grid>
                                                        <Grid container className={classes.gridContainer}>
                                                            <Grid item xs={5} lg={3} xl={3} className={classes.label}>Truck No/Name<Typography component="span" color="error">*</Typography></Grid>
                                                            <Grid item xs={7} lg={9} xl={9}>
                                                                <TextField
                                                                    disabled={formData?.transaction?.status === '50' ? true : false}
                                                                    value={formik.values?.transaction ? formik.values?.transaction?.truckNo : ""}
                                                                    name={`transaction.truckNo`}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    onChange={formik.handleChange}
                                                                    error={formik.touched.transaction && formik.touched.transaction?.truckNo && Boolean(formik.errors?.transaction?.truckNo)}
                                                                    helperText={formik.touched.transaction && formik.touched.transaction?.truckNo && Boolean(formik.errors?.transaction?.truckNo) ? formik.errors.transaction.truckNo : ""}
                                                                />
                                                            </Grid>
                                                        </Grid>
                                                        <Grid container className={classes.gridContainer}>
                                                            <Grid item xs={5} lg={3} xl={3} className={classes.label}>Driver Name<Typography component="span" color="error">*</Typography></Grid>
                                                            <Grid item xs={7} lg={9} xl={9}>
                                                                <TextField
                                                                    disabled={formData?.transaction?.status === '50' ? true : false}
                                                                    value={formik.values?.transaction ? formik.values?.transaction?.driver : ""}
                                                                    name={`transaction.driver`}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    onChange={formik.handleChange}
                                                                    error={formik.touched.transaction && formik.touched.transaction?.driver && Boolean(formik.errors?.transaction?.driver)}
                                                                    helperText={formik.touched.transaction && formik.touched.transaction?.driver && formik.touched.transaction && Boolean(formik.errors?.transaction?.driver) ? formik.errors.transaction.driver : ""}
                                                                />
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                    <Grid item xs={12} md={6} lg={4} xl={4}>
                                                        <Grid container className={classes.gridContainer}>
                                                            <Grid item xs={5} lg={4} xl={4} className={classes.label}>Weight In<Typography component="span" color="error">*</Typography></Grid>
                                                            <Grid item xs={7} lg={7} xl={8}>
                                                                <TextField
                                                                    disabled={formData?.transaction?.status === '50' ? true : false}
                                                                    value={formik.values?.transaction ? formik.values?.transaction?.weightIn : ""}
                                                                    name={`transaction.weightIn`}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    onChange={formik.handleChange}
                                                                    onBlur={formik.handleBlur}
                                                                    onKeyDown={(evt) => ["e", "E"].includes(evt.key) && evt.preventDefault()}
                                                                    error={formik.touched.transaction && formik.touched.transaction?.weightIn && formik.errors.transaction && formik.errors.transaction?.weightIn ? Boolean(formik.errors.transaction.weightIn) : false}
                                                                    helperText={formik.touched.transaction && formik.touched.transaction?.weightIn && formik.errors.transaction && formik.errors.transaction?.weightIn ? formik.errors.transaction.weightIn : ""}
                                                                />
                                                            </Grid>
                                                        </Grid>
                                                        <Grid container className={classes.gridContainer}>
                                                            <Grid item xs={5} lg={4} xl={4} className={classes.label}>Weight Out<Typography component="span" color="error">*</Typography></Grid>
                                                            <Grid item xs={7} lg={7} xl={8}>
                                                                <TextField
                                                                    disabled={formData?.transaction?.status === '50' ? true : false}
                                                                    value={formik.values?.transaction ? formik.values?.transaction?.weightOut : ""}
                                                                    name={`transaction.weightOut`}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    onChange={formik.handleChange}
                                                                    onBlur={formik.handleBlur}
                                                                    onKeyDown={(evt) => ["e", "E"].includes(evt.key) && evt.preventDefault()}
                                                                    error={formik.touched.transaction && formik.touched.transaction.weightOut && formik.errors.transaction && formik.errors.transaction?.weightOut ? Boolean(formik.errors.transaction.weightOut) : false}
                                                                    helperText={formik.touched.transaction && formik.touched.transaction.weightOut && formik.errors.transaction && formik.errors.transaction?.weightOut ? formik.errors.transaction.weightOut : ""}
                                                                />
                                                            </Grid>
                                                        </Grid>

                                                    </Grid>
                                                </Grid>
                                                <Typography component='span' color='error' style={{ float: 'right' }}>* Required</Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} sm={4} md={2} lg={2} xl={2} style={{ flexWrap: 'nowrap' }}>
                                        <Box height="100%" style={{ flexWrap: 'nowrap', overflow: 'auto' }}>
                                            <Box width="100%" height='auto' alignSelf="flex-start" justifyContent="center" style={{ flexWrap: 'wrap', flexGrow: 1 }}>
                                                {formData?.transaction?.comments ?
                                                    <Alert severity="error">{formData?.transaction?.comments}</Alert> : <></>}
                                            </Box>
                                            <Box width="100%" alignItems="flex-end" style={{ flexWrap: 'nowrap', overflow: 'auto' }}>
                                                {formData && formData?.transaction && formData?.transaction?.ticketId ?
                                                    <>
                                                        <Box style={{ flexWrap: 'nowrap', overflow: 'auto' }}>UPDATED BY: {formData?.transaction.modBy === "" ? formData?.transaction.createBy : formData.transaction.modBy}</Box>
                                                        <Box style={{ flexWrap: 'nowrap', overflow: 'auto' }}>UPDATE DATE: {formData?.transaction.modDate === "" ? new Date(formData?.transaction.createDate).toLocaleDateString('en-GB') : new Date(formData.transaction.modDate).toLocaleDateString('en-GB')}</Box>
                                                    </>
                                                    : <></>}
                                            </Box>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Form>
                        </Formik>
                    </DialogContent>
                    <DialogActions>
                        {allowAction('301', 'edit') ?
                            <Box component="span" className={classes.buttonWrapper}>
                                <Button disabled={saveButton.disabled || formData?.transaction?.status === '50'} onClick={handleEditDialogSubmit} variant="contained" color="default">Save</Button>
                                {saveButton.loading && <CircularProgress size={24} className={classes.buttonProgress} />}
                            </Box>
                            : null}
                        <Button onClick={handleEditDialogClose} variant="contained" color="default">Close</Button>
                        {formData?.transaction?.status === "10" || formData?.transaction?.status === "20" ?
                            <Box component="span" className={classes.buttonWrapper}>
                                {allowAction('301', 'submit') ?
                                    <Button disabled={reSubmitButton.disabled} onClick={handleEditDialogReSubmit} variant="contained" color="default">Submit</Button>
                                    : null}
                                {reSubmitButton.loading && <CircularProgress size={24} className={classes.buttonProgress} />}
                            </Box> : <></>}
                    </DialogActions>
                    <Backdrop className={classes.backdrop} open={isLoaded}>
                        <CircularProgress color="inherit" />
                    </Backdrop>
                </Dialog>
                <SuccessDialog successDialog={successDialog}
                    setSuccessDialog={setSuccessDialog}
                />
                <ConfirmDialog confirmDialog={confirmDialog}
                    setConfirmDialog={setConfirmDialog}
                />
                <WarnDialog warnDialog={warnDialog}
                    setWarnDialog={setWarnDialog}
                />
            </>
        );
    } else {
        return (
            <Backdrop className={classes.backdrop} open={!formReady}>
                <CircularProgress color="inherit" />
            </Backdrop>
        )
    }
}


const dayStart = startOfDay(new Date())
const dayEnd = endOfDay(new Date())

//console.log(dayStart)
//console.log(dayEnd)

const doListValidationSchema = yup.object({
    saleOrg: yup.string().required("Sales Org is required"),
    startDate: yup.date().typeError("Please click the icon to choose your date").required("Start Date is required"),
    endDate: yup.date().typeError("Please click the icon to choose your date").required("End Date is required"),
})

function DeliveryOrdersDialog(props) {
    const dispatch = useDispatch();
    const dropdownItemsStore = useSelector((state) => state.dropdownMenuItems);
    const classes = useStyles();
    //#region style-objs for Material Table --//
    const theme = useTheme()
    const headerStyle = {
        backgroundColor: '#86C1D4',
        height: 30,
        textTransform: 'uppercase',
        color: 'white',
        paddingTop: 0,
        paddingBottom: 0
    }
    const godRowStyle = {
        paddingTop: 0,
        paddingBottom: 0,
        height: 40
    }
    const parentRowStyle = {
        backgroundColor: theme.palette.type === 'light' ? theme.palette.grey[100] : theme.palette.grey[700],
        paddingTop: 0,
        paddingBottom: 0,
        height: 32
    }
    const childRowStyle = {
        backgroundColor: theme.palette.type === 'light' ? theme.palette.grey[200] : theme.palette.grey[600],
        paddingTop: 0,
        paddingBottom: 0,
        height: 24
    }
    //#endregion

    const { onClose, open, isEditing, formData, setFormData, handleSetDoItems, onCancelWithoutSelection, resetForm, setTabValue } = props;

    //Table-Independent State
    const [autocompleteData, setAutocompleteData] = useState({})
    const [fetchItem, setFetchItem] = useState([]);
    const [showTableLoading, setShowTableLoading] = useState(false);
    const [searchFormData, setSearchFormData] = useState({
        saleOrg: "",
        startDate: dayStart,
        endDate: dayEnd
    })
    const formik = useFormik({
        initialValues: searchFormData,
        validationSchema: doListValidationSchema,
        onSubmit: values => {
            if (typeof values.startDate === 'object') {
                let date = format(values.startDate, 'yyyy-MM-dd');
                let time = format(values.startDate, 'HH:mm:ss');
                values.startDate = date + 'T' + time;
            }
            if (typeof values.endDate === 'object') {
                let date = format(values.endDate, 'yyyy-MM-dd');
                let time = format(values.endDate, 'HH:mm:ss');
                values.endDate = date + 'T' + time;
            }
            try {
                fetchTableData(values);
            } catch (e) {
                console.log(e);
            }
        }
    });
    //console.log('sfd', searchFormData)
    //console.log(formik.values)
    const handleSearchSubmit = () => {
        if (Object.keys(formik.errors).length === 0) {
            setShowTableLoading(true);
        }
        //console.log('formikErrorsDoList', formik.errors)
        formik.handleSubmit()
    };
    //Material Table State Management
    const [selectedRow, setSelectedRow] = useState(null);
    const [selectedCheckbox, setSelectedCheckbox] = useState(null);

    function resetListForm() {
        setSearchFormData({
            saleOrg: "",
            startDate: dayStart,
            endDate: dayEnd
        })
        formik.resetForm();
        setSelectedCheckbox(null);
        setAutocompleteData({});
        setFetchItem([]);
    }

    async function fetchTableData(formData) {
        DeliveryOrdersService.searchDeliveryOrders(formData).then(
            (response) => {
                if (response) {
                    setFetchItem(response.data);
                }
                setShowTableLoading(false);
            });
    };

    //#region columns for MUI DATAGRID
    //const columns = [
    //    {
    //        field: 'doNo',
    //        headerName: 'Do No',
    //        width: 130,
    //        headerClassName: 'delivery-orders-table--header'
    //    },
    //    {
    //        field: 'doItem',
    //        headerName: 'Do Item',
    //        width: 100,
    //        headerClassName: 'delivery-orders-table--header'
    //    },
    //    {
    //        field: 'saleOrg',
    //        headerName: 'Sales Org',
    //        width: 120,
    //        headerClassName: 'delivery-orders-table--header'
    //    },
    //    {
    //        field: 'matCode',
    //        headerName: 'Material No',
    //        width: 140,
    //        headerClassName: 'delivery-orders-table--header'
    //    },
    //    {
    //        field: 'matDesc',
    //        headerName: 'Material Name',
    //        flex: 1,
    //        headerClassName: 'delivery-orders-table--header'
    //    },
    //    {
    //        field: 'batchNo',
    //        headerName: 'Batch',
    //        width: 150,
    //        headerClassName: 'delivery-orders-table--header'
    //    },
    //    {
    //        field: 'netWeight',
    //        headerName: 'Net Weight',
    //        width: 120,
    //        align: 'right',
    //        headerClassName: 'delivery-orders-table--header'
    //    },
    //    {
    //        field: 'weightUnit',
    //        headerName: 'Unit',
    //        width: 80,
    //        align: 'right',
    //        headerClassName: 'delivery-orders-table--header'
    //    },
    //];
    //#endregion

    //#region columns for Material Table
    const columns = [
        {
            field: 'doNo',
            title: 'Do No',
            //width: 130,
        },
        {
            field: 'ticketId',
            title: 'Ticket ID'
        },
        {
            field: 'doItem',
            title: 'Do Item',
            //width: 100,
        },
        {
            field: 'saleOrg',
            title: 'Sales Org',
            //width: 120,
        },
        {
            field: 'matCode',
            title: 'Material No',
            //width: 140,
        },
        {
            field: 'matDesc',
            title: 'Material Name',
            //flex: 1,
        },
        {
            field: 'batchNo',
            title: 'Batch',
            //width: 150,
        },
        {
            field: 'netWeight',
            title: 'Net Weight',
            //width: 120,
        },
        {
            field: 'weightUnit',
            title: 'Unit',
            //width: 80,
        },
    ];
    //#endregion

    //#region PREPARE PARENT/CHILD DATA FROM FETCHED DATA FOR MATERIAL TABLE
    function getPreparedRows(fetchItem) {
        function findChildren(fetchedData, iteratingData) {
            let found = [];
            for (let i = 0; i < fetchedData.length; i++) {
                if (fetchedData[i].doNo === iteratingData.doNo && fetchedData[i].doItem.substring(0, 1) === '9' && iteratingData.doItem === fetchedData[i].higerLevelItem && iteratingData.ticketId === fetchedData[i].ticketId) {
                    found.push(`${fetchedData[i].ticketId}` + ':' + `${fetchedData[i].doNo}` + `${fetchedData[i].doItem}`);
                    //break;
                }
            }
            return found
        }

        let createGodDos = [];
        for (let i = 0; i < fetchItem.length; i++) {
            if (fetchItem[i].higerLevelItem === '000000') {
                let exists = createGodDos.find(x => (x.id === 'god' + '|-|' + fetchItem[i].doNo + '|-|' + fetchItem[i].ticketId) && x.ticketId === fetchItem[i].ticketId)
                if (exists === undefined) {
                    createGodDos.push({
                        doNo: fetchItem[i].doNo,
                        id: 'god' + '|-|' + fetchItem[i].doNo + '|-|' + fetchItem[i].ticketId,
                        isGod: true,
                        higerLevelItem: 'god',
                        ticketId: fetchItem[i].ticketId
                    })
                }
            }
        }
        let doFamily = [...createGodDos, ...fetchItem]
        let addChildTags = [];
        for (let i = 0; i < doFamily.length; i++) {
            if (doFamily[i].higerLevelItem === 'god') {
                addChildTags.push({ ...doFamily[i] })

            } else if (doFamily[i].higerLevelItem === '000000') {
                let childrenFound = findChildren(fetchItem, doFamily[i]);
                addChildTags.push({
                    ...doFamily[i],
                    id: doFamily[i].ticketId + '|-|' + doFamily[i].higerLevelItem + '|-|' + doFamily[i].doNo + '|-|' + doFamily[i].doItem,
                    isParent: true,
                    hasChildren: childrenFound.length > 0 ? true : false,
                    children: childrenFound,
                    parentId: 'god' + '|-|' + doFamily[i].doNo + '|-|' + doFamily[i].ticketId
                })
            } else if (Number(doFamily[i].higerLevelItem) > 0) {
                addChildTags.push({
                    ...doFamily[i],
                    id: doFamily[i].ticketId + '|-|' + doFamily[i].higerLevelItem + '|-|' + doFamily[i].doNo + '|-|' + doFamily[i].doItem,
                    isParent: false,
                    parentId: doFamily[i].ticketId + '|-|' + '000000' + '|-|' + doFamily[i].doNo + '|-|' + doFamily[i].higerLevelItem
                })
            } else {
                console.log('Something is wrong with the DO LIST tree')
            }
        }
        for (let i = 0; i < addChildTags.length; i++) {
            if (addChildTags[i].hasChildren) {
                addChildTags[i] = {
                    ...addChildTags[i], matCode: null, matDesc: null, netWeight: null, batchNo: null, weightUnit: null
                }
            }
        }
        return addChildTags;
    }
    //#endregion
    //Pass modified data to Material Table
    const rows = useMemo(() => getPreparedRows(fetchItem), [fetchItem]);
    //console.log(rows)

    //#region Handlers
    const handleAutoCompleteChange = (target, fieldName) => {
        var value = target !== null ? target.value : ''
        var text = target !== null ? target.text : ''
        setAutocompleteData({
            ...autocompleteData,
            [fieldName]: { text: text, value: value }
        })
        setSearchFormData({ ...searchFormData, [fieldName]: value });
        formik.setFieldValue("saleOrg", value)
    };
    const handleDateStartChange = (date) => {
        setSearchFormData({ ...searchFormData, startDate: date });
        formik.setFieldValue("startDate", date)
    };
    const handleDateEndChange = (date) => {
        setSearchFormData({ ...searchFormData, endDate: date });
        formik.setFieldValue("endDate", date)
    };
    const handleCancelClick = () => {
        if (formData?.doItems?.length > 0) {
            dispatch({
                type: SHOW_DO_LIST,
                payload: false,
            });
            resetListForm();
            onClose();
        } else {
            resetListForm();
            onCancelWithoutSelection();
        }
    };
    const handleClose = () => {
        dispatch({
            type: SHOW_DO_LIST,
            payload: false,
        });
        resetListForm();
        onClose();
    };
    const handleSelectedCheckboxChange = (event) => {
        if (selectedCheckbox === event.target.value) {
            setSelectedCheckbox(null);
        } else {
            setSelectedCheckbox(event.target.value)
        }
    };

    const handleSelectDoAndItems = (doNo, ticketId) => {
        var selectedDo = fetchItem.filter(function (item) {
            if (ticketId === 'null') {
                return item.doNo === doNo
            }
            return item.doNo === doNo && item.ticketId === ticketId;
        });
        //console.group('handleSelectDoAndItems --- DO LIST DIALOG')
        //console.log('%c selectedDo', 'background-color:orange;color:purple', selectedDo)

        //LOADING DATE WILL FOLLOW USER SELECTED END DATE: FOR NOW WILL DEFAULT TO SAME AS END DATE DEFAULT: KEEP THIS IF NEEDED IN FUTURE
        let loadDateForDo = new Date();
        //if (selectedDo[0].loadDate) {
        //    let date = selectedDo[0].loadDate.substring(0, 10).split('/'); // ---> ['mm', 'dd', 'yyyy']
        //    let hours;
        //    let mins;
        //    let secs;
        //    if (selectedDo[0].loadTime) {
        //        let loadTime = selectedDo[0].loadTime;
        //        hours = loadTime.substring(0, 2);
        //        mins = loadTime.substring(2, 4);
        //        secs = loadTime.substring(4, 6);
        //    } else {
        //        hours = '00';
        //        mins = '00';
        //        secs = '00';
        //    }
        //    //note: months are 0 indexed
        //    //format passed to new Date() for conversion: 'yyyy', 'mm', 'dd', 'hh', 'mm', 'ss'
        //    loadDateForDo = new Date(date[2], date[0] - 1, date[1], hours, mins, secs);
        //} else {
        //    loadDateForDo = new Date()
        //}
        let transaction;
        let doItems = [];
        let sapDo = [];
        if (!isEditing) {
            //console.log('not editing formData.transaction', formData.transaction)
            transaction = {
                ...formData.transaction,
                transactionType: 'Sale',
                status: '',
                doNo: doNo,
                bagLq: '',
                shipmentType: '',
                loadingDate: loadDateForDo,
                sealNo: '',
                weightIn: '',
                weightOut: '',
                weightInDatetime: new Date(),
                weightOutDatetime: new Date(),
                driver: '',
                truckNo: '',
                transportCom: '',
                coaNo: '',
                doInsTxt: ''
            };
            //resetForm({...formData.transaction})
        } else {
            //When editing, if the user changes DO, we want to replace the DO level information
            transaction = {
                ...formData.transaction,
                transactionType: 'Sale',
                doNo: doNo,
                loadingDate: loadDateForDo,
            }
        }

        let parents = [];
        //compare child's higerLevelItem string to each selectedDo.doItem. 
        //If match means parent and child items both exist in selectedDo array and we want to filter out the parent
        selectedDo.forEach(x => {
            if (x.doItem.substring(0, 1) === '9' && !parents.includes(x.higerLevelItem)) {
                parents.push(x.higerLevelItem)
            }
        })
        for (var i in selectedDo) {
            //if item does not have a parent, push it into doItems array to show as form tab
            if (!parents.includes(selectedDo[i].doItem)) {
                doItems.push({
                    doItem: selectedDo[i].doItem,
                    slocFrom: selectedDo[i].storageLoc,
                    higherLevelItem: selectedDo[i].higerLevelItem,
                    baseUnit: selectedDo[i].baseUom,
                    salesUnit: selectedDo[i].saleUnit,
                    actQtyDelvSu: selectedDo[i].actQtyDelvSu,
                    //actQtyDelvSku: selectedDo[i].actQtyDelvSku,
                    pickQtySu: selectedDo[i].pickQtySu,
                    plantFrom: selectedDo[i].plant,
                    materialNo: selectedDo[i].matCode,
                    materialName: selectedDo[i].matDesc,
                    netWeight: selectedDo[i].netWeight,
                    grossWeight: selectedDo[i].grossWeight,
                    weightUnit: selectedDo[i].weightUnit,
                    batchNoFrom: selectedDo[i].batchNo,
                    batchIndicator: selectedDo[i].batchIndicator,
                    //doInsTxt: selectedDo[i].deliveryInstructionText --move to DO level with coaNo
                });
                sapDo.push({
                    salesOrg: selectedDo[i].saleOrg,
                    salesUnit: selectedDo[i].salesUnit,
                    actQtyDelvSu: selectedDo[i].actQtyDelvSu,
                    //actQtyDelvSku: selectedDo[i].pickQtySu,
                    plant: selectedDo[i].plant,
                    sdDocCat: selectedDo[i].sdDocCat,
                });
            }
        }
        //console.groupEnd();
        setFormData({ transaction, doItems, sapDo });
        handleSetDoItems({ transaction, doItems, sapDo });
        setTabValue(0)
    };
    const handleSelectDoSubmit = () => {
        if (selectedCheckbox) {
            /////////////////////////////////////////gets doNo//////////////////////gets ticketId if any, else 'null'      
            handleSelectDoAndItems(selectedCheckbox.substring(6, 16), selectedCheckbox.substring(19));
            dispatch({
                type: SHOW_DO_LIST,
                payload: false,
            });
            resetListForm();
            handleClose();
        } else {
            alert("Please select a DO")
        }
    };

    //#endregion


    return (
        <Dialog
            fullWidth={true}
            maxWidth="lg"
            onClose={handleClose}
            aria-labelledby="delivery-order-dialog"
            disableBackdropClick
            open={open}>
            <DialogTitle id="delivery-order-dialog" disableTypography>
                <Typography variant='h5' color='textPrimary'>DO List</Typography>
            </DialogTitle>
            <div style={{ paddingLeft: 20, paddingBottom: 10 }}>
                <Formik>
                    <Form>
                        <Grid container spacing={1} className={classes.inputGridContainer}>
                            <Grid item xs={12} md={3} className={classes.gridItem}>
                                <Autocomplete
                                    //closeIcon=""
                                    disabled={false}
                                    //style={{ width: 260 }}
                                    fullWidth
                                    options={dropdownItemsStore.salesOrg}
                                    value={autocompleteData.saleOrg ? { text: autocompleteData.saleOrg.text, value: autocompleteData.saleOrg.value } : ""}
                                    getOptionLabel={(option) => option.text ? option.text : ''}
                                    getOptionSelected={(option, value) => option.value === value.value}
                                    renderOption={option => <>{option.text}</>}
                                    onChange={(event, newValue) => {
                                        handleAutoCompleteChange(newValue, 'saleOrg');
                                    }}
                                    renderInput={(params) => <TextField {...params}
                                        size="small"
                                        variant="outlined"
                                        label="Sales Org"
                                        error={formik.touched.saleOrg && Boolean(formik.errors.saleOrg)}
                                        helperText={formik.touched.saleOrg && formik.errors.saleOrg}
                                    />}
                                />
                            </Grid>
                            <Grid item xs={12} md={3} className={classes.gridItem}>
                                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                    <KeyboardDatePicker
                                        disableToolbar
                                        autoOk={true}
                                        fullWidth
                                        size="small"
                                        inputVariant="outlined"
                                        format="dd-MMM-yyyy"
                                        id="startDate"
                                        name="startDate"
                                        label="Start Date"
                                        maxDate={formik.values?.endDate ? formik.values.endDate : dayStart}
                                        maxDateMessage={'Start Date cannot come after the End Date'}
                                        value={formik.values?.startDate ? formik.values.startDate : dayStart}
                                        onChange={handleDateStartChange}
                                        keyboardIcon={<Calendar />}
                                        error={formik.touched?.startDate && Boolean(formik.errors?.startDate) ? Boolean(formik.errors?.startDate) : false}
                                        helperText={formik.touched?.startDate && Boolean(formik.errors?.startDate) ? formik.errors?.startDate : ''}
                                    />
                                </MuiPickersUtilsProvider>
                            </Grid>
                            <Grid item sm={12} md={3} className={classes.gridItem}>
                                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                    <KeyboardDatePicker
                                        disableToolbar
                                        autoOk={true}
                                        fullWidth
                                        size="small"
                                        inputVariant="outlined"
                                        format="dd-MMM-yyyy"
                                        minDate={formik.values?.startDate ? formik.values.startDate : dayEnd}
                                        minDateMessage={'End Date cannot come before the Start Date'}
                                        id="endDate"
                                        name="endDate"
                                        label="End Date"
                                        value={formik.values?.endDate ? formik.values.endDate : dayEnd}
                                        onChange={handleDateEndChange}
                                        keyboardIcon={<Calendar />}
                                        error={formik.touched.endDate && Boolean(formik.errors.endDate) ? Boolean(formik.errors.endDate) : false}
                                        helperText={formik.touched.endDate && Boolean(formik.errors.endDate) ? formik.errors.endDate : ''}
                                    />
                                </MuiPickersUtilsProvider>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Grid
                                    container
                                    direction="row"
                                    justify="center"
                                    alignItems="center"
                                >
                                    <Button onClick={handleSearchSubmit} variant="contained" color="primary" size='small'>Search</Button>
                                    <Button
                                        onClick={handleSelectDoSubmit}
                                        variant="contained"
                                        size='small'
                                        style={{ marginLeft: 10, marginRight: 10 }}
                                        color="primary">
                                        Select
                            </Button>
                                    <Button
                                        onClick={handleCancelClick}
                                        variant="contained"
                                        size='small'
                                        style={{ marginRight: 10 }}
                                        color="default">
                                        Cancel
                            </Button>

                                </Grid>
                            </Grid>
                        </Grid>
                    </Form>
                </Formik>
            </div>
            <MaterialTable
                style={{ width: '100%' }}
                title=""
                icons={TableIcons}
                isLoading={showTableLoading}
                components={{
                    Toolbar: props => (
                        <div style={{ width: 0, height: 0 }}>
                            <MTableToolbar {...props} />
                        </div>
                    ),
                    Header: props => (
                        <>
                            <MTableHeader {...props} />
                        </>
                    ),
                }}
                data={rows}
                columns={columns}
                parentChildData={(row, rows) => rows.find(a => {
                    return a.id === row.parentId
                })
                }
                actions={
                    [
                        rowData => {
                            if (rowData.isGod) {
                                return ({
                                    icon: () => {
                                        return (
                                            <Checkbox
                                                size='medium'
                                                color='default'
                                                value={rowData.id}
                                                name={rowData.id}
                                                checked={selectedCheckbox === rowData.id}
                                                onChange={handleSelectedCheckboxChange}
                                            />
                                        )
                                    },
                                    iconProps: {
                                        fontSize: 'small',
                                    },
                                })
                            }
                        },
                    ]}
                onRowClick={((evt, selectedRow) => setSelectedRow(selectedRow.tableData.id))}
                options={{
                    actionsCellStyle: { paddingLeft: 0, paddingRight: 10 },
                    //actionsColumnIndex: 1,
                    /* We will use the client side sorting/pagination as the SAP API doesn't handle the pagination */
                    sorting: true,
                    headerStyle: headerStyle,
                    rowStyle: rowData => {
                        if (rowData.isGod) {
                            return godRowStyle;
                        } else if (rowData.isParent) {
                            return parentRowStyle
                        } else {
                            return childRowStyle;
                        }
                    },
                    cellStyle: { paddingTop: 0, paddingBottom: 0 },
                    maxBodyHeight: 530,
                    showTitle: false,
                    toolbar: false,
                    paging: true,
                    pageSize: 10,
                    //selection: true,
                    //showSelectAllCheckbox: false,
                    //grouping: true
                    //emptyRowsWhenPaging: false,
                    //pageSizeOptions: [10, 20, 50],
                    //filtering: true,
                    //search: true
                    //grouping: true
                }}
            />

        </Dialog>
    );
}

